import { Row, LinkListHeading, FlexBox, GridContainer, Column } from '@vp/swan'
import './ArticleToc.scss'
import { useTranslation } from 'react-i18next'
import { useEffect, useState, useCallback, useRef } from 'react'

export interface TocHeading {
  text: string
  id: string
}

interface ArticleTocProps {
  tenant: string
  locale: string
  articleBody: string
}

export const ArticleToc = (props: ArticleTocProps) => {
  const { articleBody, locale, tenant } = props
  const { t } = useTranslation(tenant)
  const lng = locale.toLowerCase().replace('-', '_')

  const [headings, setHeadings] = useState<TocHeading[]>([])
  const [activeHeading, setActiveHeading] = useState<string | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const isClickScrollingRef = useRef(false)

  useEffect(() => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(articleBody, 'text/html')
    const h2Elements = Array.from(doc.querySelectorAll('h2'))

    const extractedHeadings: TocHeading[] = h2Elements
      .map((h2, index) => {
        const text = h2.textContent?.trim() || ''
        if (!text) return null

        let id = h2.id
        if (!id) {
          id = text
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/--+/g, '-')
            .trim()
          id = id ? `${id}-${index + 1}` : `heading-${index + 1}`
        }

        return { text, id }
      })
      .filter(Boolean) as TocHeading[]

    setHeadings(extractedHeadings)

    if (extractedHeadings.length > 0 && !activeHeading) {
      setActiveHeading(extractedHeadings[0].id)
    }
  }, [articleBody])

  useEffect(() => {
    if (headings.length <= 1) return

    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    const options = {
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0,
    }

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      if (isClickScrollingRef.current) return

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveHeading(entry.target.id)
        }
      })
    }

    const setupObserver = () => {
      observerRef.current = new IntersectionObserver(observerCallback, options)

      headings.forEach((heading) => {
        const element = document.getElementById(heading.id)
        if (element) {
          observerRef.current!.observe(element)
        }
      })
    }

    const timeoutId = setTimeout(setupObserver, 100)

    return () => {
      clearTimeout(timeoutId)
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [headings])

  const handleHeadingClick = useCallback((headingId: string) => {
    const element = document.getElementById(headingId)
    if (element) {
      isClickScrollingRef.current = true
      setActiveHeading(headingId)

      const offset = 80
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })

      setTimeout(() => {
        isClickScrollingRef.current = false
      }, 1000)
    }
  }, [])

  if (headings.length <= 1) {
    return null
  }

  return (
    <GridContainer className="article-toc-sticky-wrapper">
      <Row className="article-toc-container">
        <Column span={12} className="heading-wrapper">
          <LinkListHeading id="articleToc">{t(`${tenant}:articles.in_article`, { lng }) || 'In this guide'}</LinkListHeading>
          <FlexBox className="toc-list">
            {headings.map((heading, index) => (
              <button
                key={heading.id}
                type="button"
                className={`toc-item ${activeHeading === heading.id ? 'active' : ''}`}
                onClick={() => handleHeadingClick(heading.id)}
                aria-label={`Navigate to section: ${heading.text}`}
                aria-current={activeHeading === heading.id ? 'location' : undefined}
              >
                <span className="toc-number">{index + 1}</span>
                <span className="toc-text">{heading.text}</span>
              </button>
            ))}
          </FlexBox>
        </Column>
      </Row>
    </GridContainer>
  )
}
